import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import client from "./client";
import API_ENDPOINTS from "../constants/api-endpoints";
import { TCompany, TCreateCompanyInput, TPlatformStats } from "../types";

type CompanyListParams = { key?: string; status?: string; page?: number; limit?: number };
type SaveCallbacks = { onSuccess?: (res: any) => void; onError?: (err: any) => void };

export const useCompanyStats = () => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_STATS],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_STATS),
    select: (res: any) => res.data.data as TPlatformStats,
    refetchOnWindowFocus: false,
  });
};

export const useCompanyList = (qData: CompanyListParams = { page: 1, limit: 50 }) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY, qData],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY, { params: qData }),
    select: (res: any) => res.data.data as TCompany[],
    refetchOnWindowFocus: false,
  });
};

export const useCompanyDetailForEdit = (id: number | null) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.COMPANY, id, "forEdit"],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY + id!),
    select: (res: any) => res.data.data as TCompany,
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
};

export const useCompanySave = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TCreateCompanyInput) => client.post(API_ENDPOINTS.COMPANY, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_STATS] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyUpdate = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TCompany> }) =>
      client.put(API_ENDPOINTS.COMPANY + id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyToggleActive = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      client.patch(API_ENDPOINTS.COMPANY_TOGGLE_ACTIVE(id), { is_active }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_STATS] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyExtendTrial = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, days }: { id: number; days: number }) =>
      client.patch(API_ENDPOINTS.COMPANY_EXTEND_TRIAL(id), { days }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyDelete = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => client.delete(API_ENDPOINTS.COMPANY_DELETE(id)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY_STATS] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useCompanyChangePlan = ({ onSuccess, onError }: SaveCallbacks = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, plan_id }: { id: number; plan_id: number }) =>
      client.patch(API_ENDPOINTS.COMPANY_CHANGE_PLAN(id), { plan_id }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.COMPANY] });
      onSuccess?.(res);
    },
    onError: (err) => onError?.(err),
  });
};

export const useFleetOverview = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_FLEET_OVERVIEW],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_FLEET_OVERVIEW),
    select: (res: any) => res.data.data as any[],
    refetchOnWindowFocus: false,
  });

export const useComplianceOverview = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_COMPLIANCE_OVERVIEW],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_COMPLIANCE_OVERVIEW),
    select: (res: any) => res.data.data as { dbs: any[]; rtw: any[] },
    refetchOnWindowFocus: false,
  });

export const useOnboardingOverview = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_ONBOARDING_OVERVIEW],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_ONBOARDING_OVERVIEW),
    select: (res: any) => res.data.data as any[],
    refetchOnWindowFocus: false,
  });

export const usePipelineOverview = () =>
  useQuery({
    queryKey: [API_ENDPOINTS.COMPANY_PIPELINE_OVERVIEW],
    queryFn: () => client.get(API_ENDPOINTS.COMPANY_PIPELINE_OVERVIEW),
    select: (res: any) => res.data.data as any[],
    refetchOnWindowFocus: false,
  });
